import type { EraId } from '../config/eras';
import type { LapCardFacts } from '../net/discord';
import { shortLap } from '../net/lapTime';
import { type PosterPalette, posterStyleFor } from '../ui/posterStyle';

/**
 * A best lap's picture for its Discord message (1200 × 630): the day's print and car (gold for yesterday's winner),
 * the time as big as it fits, its three sectors in their colours, its medal and its place in the server. Laid out
 * here once, as shapes and lines of text, for both the ways it's drawn: by the server itself from a template (the
 * car's ready-made picture, worker/lapTemplate.ts) and by the game's canvas in a headless browser when that can't
 * be done (src/render/shareCard.ts drawLapCard). Nothing here touches a page.
 */
export interface LapCard {
  era: EraId;
  palette: PosterPalette;
  /** "merky's best today" */
  label: string;
  /** "36.380" */
  time: string;
  /** Each sector's time and colour (purple: the server's fastest today). */
  sectors: { time: string; color: 'purple' | 'green' | 'yellow' | 'invalid' | null }[];
  /** The best medal it earned, or null. */
  medal: 'bronze' | 'silver' | 'gold' | 'author' | null;
  /** "circuito di torrebianca · the sharknose 1961" */
  where: string;
  /** Yesterday's winner: their car is gold. */
  gold?: boolean;
  /** "P1 of 3": their place in the server, beside the medal. */
  place?: string;
}

export const LAP_CARD_W = 1200;
export const LAP_CARD_H = 630;
const BORDER = 20;
export const CREAM = '#F3E6C8';
/** Where the day's car stands on the card, and the size its picture is drawn at. */
export const LAP_CARD_CAR = { x: 540, y: 70, w: 680, h: 540 } as const;
/** The print inside the cream border: the sun and the car are cut to it. */
export const LAP_CARD_FIELD = { x: BORDER, y: BORDER, w: LAP_CARD_W - 2 * BORDER, h: LAP_CARD_H - 2 * BORDER } as const;
const SECTOR_FILL = { purple: '#6E3A64', green: '#2F4A2E', yellow: '#EDC27A', invalid: '#8C7B6B' } as const;
const MEDAL_FILL = { bronze: '#B0673C', silver: '#9D968A', gold: '#DDA53A', author: '#6E3A64' } as const;
const FOOTER = 'GRAND PREMIO · RACE THIS LAP IN DISCORD';

/** The two typefaces: the posters' tall display letters (League Gothic) and the condensed text (Barlow Condensed). */
export type Face = 'display' | 'text';

/** A line of text's width in pixels, in a face, weight and size. */
export type Measure = (text: string, face: Face, weight: number, size: number) => number;

/**
 * One step of drawing the card, in order. `clip`: cut to the print inside the border. `day`: the same on every card
 * of that day and car (the print, the sun, the car, where, the footer), so the server draws it once and keeps it.
 */
export type CardOp =
  | { op: 'rect'; x: number; y: number; w: number; h: number; fill?: string; stroke?: string; line?: number; day?: true }
  | { op: 'circle'; x: number; y: number; r: number; fill: string; alpha?: number; stroke?: string; line?: number; clip?: true; day?: true }
  | { op: 'car'; clip: true; day: true }
  | { op: 'text'; x: number; y: number; text: string; face: Face; weight: number; size: number; fill: string; spacing?: number; day?: true };

/** The card for a lap as the server sees it (src/net/discord.ts LapCardFacts). */
export function lapCardOf(f: LapCardFacts): LapCard {
  return {
    era: f.era,
    palette: posterStyleFor(f.date, f.era).palette,
    label: `${f.name}’s best today`.toLowerCase(),
    time: shortLap(f.lapMs),
    sectors: f.sectors.map((s) => ({ time: shortLap(s.ms), color: s.fastest ? ('purple' as const) : null })),
    medal: f.medal,
    where: f.where,
    gold: f.gold,
    ...(f.place ? { place: `P${f.place.rank} of ${f.place.of}` } : {}),
  };
}

/** Letter-spaced text's width: a letter at a time, as it's drawn (not every browser spaces canvas text). */
export function spacedWidth(measure: Measure, text: string, face: Face, weight: number, size: number, gap: number): number {
  let w = 0;
  for (const ch of text) w += measure(ch, face, weight, size) + gap;
  return w - gap;
}

/** The card, step by step. */
export function lapCardOps(o: LapCard, measure: Measure): CardOp[] {
  const p = o.palette;
  // The largest size (up to `max`) at which the text fits `width`.
  const fit = (text: string, width: number, max: number, face: Face): number => {
    const w = measure(text, face, face === 'display' ? 400 : 600, max);
    return w <= width ? max : Math.floor((max * width) / w);
  };
  const ops: CardOp[] = [
    { op: 'rect', x: 0, y: 0, w: LAP_CARD_W, h: LAP_CARD_H, fill: CREAM, day: true },
    { op: 'rect', ...LAP_CARD_FIELD, fill: p.field, day: true },
    { op: 'circle', x: 890, y: 330, r: 250, fill: p.sun, alpha: 0.92, clip: true, day: true },
    { op: 'car', clip: true, day: true },
  ];
  const x = 68;
  // The red label: whose best it is.
  const label = o.label.toUpperCase();
  const lw = spacedWidth(measure, label, 'text', 700, 30, 3.5);
  ops.push({ op: 'rect', x, y: 62, w: lw + 36, h: 50, fill: p.accent });
  ops.push({ op: 'text', x: x + 18, y: 98, text: label, face: 'text', weight: 700, size: 30, fill: p.accentText, spacing: 3.5 });
  // The time, as big as it fits.
  ops.push({ op: 'text', x: x - 6, y: 330, text: o.time, face: 'display', weight: 400, size: fit(o.time, 470, 250, 'display'), fill: p.title });
  // The three sectors, in their colours.
  let sx = x;
  o.sectors.forEach((s, i) => {
    const t = `S${i + 1} ${s.time}`;
    const w = measure(t, 'text', 700, 26) + 28;
    ops.push({ op: 'rect', x: sx, y: 362, w, h: 44, fill: s.color ? SECTOR_FILL[s.color] : 'rgba(59,42,30,0.55)' });
    // A thin cream edge: a chip the colour of the day's field still reads as one.
    ops.push({ op: 'rect', x: sx + 1, y: 363, w: w - 2, h: 42, stroke: 'rgba(243,230,200,0.55)', line: 2 });
    ops.push({ op: 'text', x: sx + 14, y: 393, text: t, face: 'text', weight: 700, size: 26, fill: s.color === 'yellow' ? '#3B2A1E' : CREAM });
    sx += w + 10;
  });
  // The medal, a disc with its name, and the place in the server beside it.
  const cy = 466;
  let mx = x;
  if (o.medal) {
    const cx = x + 34;
    ops.push({ op: 'circle', x: cx, y: cy, r: 30, fill: MEDAL_FILL[o.medal], stroke: 'rgba(59,42,30,0.35)', line: 3 });
    mx = cx + 46;
  }
  const medalLine = [o.medal ? `${o.medal} medal` : '', o.place ?? ''].filter(Boolean).join(' · ');
  if (medalLine) ops.push({ op: 'text', x: mx, y: cy + 16, text: medalLine, face: 'display', weight: 400, size: fit(medalLine, 470 - (mx - x), 46, 'display'), fill: p.ink });
  ops.push({ op: 'text', x, y: 553, text: o.where, face: 'text', weight: 600, size: fit(o.where, 470, 28, 'text'), fill: p.text, day: true });
  ops.push({ op: 'text', x, y: 592, text: FOOTER, face: 'text', weight: 700, size: 21, fill: p.ink, spacing: 2.6, day: true });
  return ops;
}
